import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box, LinearProgress } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { loginRequest } from 'authConfig';
import { GlobalContext } from 'globalContext/GlobalContext';
import { ModalProvider } from 'globalContext/ModalContext';
import { UserContext } from 'globalContext/UserContext';
import moment from 'moment';
import { getNavigationItems } from 'router/navigation';
import { renderLegacyRedirects } from 'router/redirects';
import routes from 'router/routes';
import { userInfoUrl } from 'router/url';
import { ToastProvider } from 'components';
import Drawer from 'components/Drawer/Drawer';
import { BodyWrapper } from 'components/Drawer/Drawer.css';
import Header from 'components/Header/Header';
import Modal from 'components/Modal/Modal';
import { fetchEmploymentForm } from 'modules/EmploymentForms/api';
import { getOrderPermissions } from 'modules/Orders/api';
import { isWorkOrdersPath } from 'modules/Orders/workOrderNavigation';
import WorkOrderTabs from 'modules/Orders/WorkOrderTabs';
import { getFetch } from 'utils/fetchFunctions';
import MondayMomentUtils from 'utils/MondayMomentUtils';
import { removeScript } from 'utils/removeScript';
import 'moment/locale/en-gb';
import { RoleTypes } from './utils/rolesTypes';
import './App.css';

function App() {
  const { dispatch, teamLeader, roles, username } = useContext(UserContext);
  const [openMenu, setOpenMenu] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [orderPermissions, setOrderPermissions] = useState({
    canManage: false,
    canViewAll: false,
    canViewOwn: false,
  });
  const [canAccessEmploymentForms, setCanAccessEmploymentForms] =
    useState(false);

  const fetchUser = useCallback(async () => {
    const response = await getFetch({
      url: userInfoUrl,
      setLoading,
      showErrorToast: false,
    });

    if (response?.username && response?.roles) {
      dispatch({
        type: 'ADD_GLOBAL_DATA',
        payload: {
          username: response.username,
          teamLeader: response.teamLeader,
          roles: response.roles,
        },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || isAuthenticating) {
      return;
    }

    const account = instance.getActiveAccount() ?? accounts[0];
    if (account && isAuthenticated) {
      instance.setActiveAccount(account);
      fetchUser();
      return;
    }

    setIsAuthenticating(true);
    instance
      .loginRedirect(loginRequest)
      .catch((error) => console.error(error))
      .finally(() => setIsAuthenticating(false));
  }, [
    accounts,
    instance,
    isAuthenticated,
    inProgress,
    isAuthenticating,
    fetchUser,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !roles.length) {
      setOrderPermissions({
        canManage: false,
        canViewAll: false,
        canViewOwn: false,
      });
      return;
    }

    let mounted = true;
    getOrderPermissions().then(({ canManage, canViewAll, canViewOwn }) => {
      if (mounted) {
        setOrderPermissions({ canManage, canViewAll, canViewOwn });
      }
    });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, roles]);

  useEffect(() => {
    if (!isAuthenticated || !roles.length || !username) {
      setCanAccessEmploymentForms(false);
      return;
    }
    if (
      roles.includes(RoleTypes.RoleAccounting) ||
      roles.includes(RoleTypes.RoleDEVHR)
    ) {
      setCanAccessEmploymentForms(true);
      return;
    }

    let mounted = true;
    fetchEmploymentForm(username).then((result) => {
      if (mounted) {
        setCanAccessEmploymentForms(result.ok);
      }
    });
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, roles, username]);

  const toggleDrawer = () => setOpenMenu((prevState) => !prevState);

  const workOrderAccess = {
    ...orderPermissions,
    canEditEmploymentForms: canAccessEmploymentForms,
  };

  const getDrawerData = () =>
    getNavigationItems(
      workOrderAccess,
      hideTabsForPrivilegedUsers && roles.length > 0,
    );

  const {
    localization: { locales, currentLocale },
    setlogoutURL,
  } = useContext(GlobalContext);

  const history = useHistory();

  useEffect(() => {
    if (location.pathname !== '' && !isAuthenticated) {
      setlogoutURL(location.pathname + location.search);
    }
  }, [history, isAuthenticated, location, setlogoutURL]);

  const logOut = () => {
    const logoutRequest = {
      account: instance.getActiveAccount() ?? accounts[0],
    };
    instance
      .logoutRedirect(logoutRequest)
      .then(() => {
        removeScript();
        setlogoutURL('');
      })
      .catch((e) => {
        console.error(e);
      });
  };

  //If you want to change authorized users, change it also in the Login form!
  const hideTabsForPrivilegedUsers = useMemo(() => {
    return !(
      roles.includes(RoleTypes.RoleAccounting) ||
      roles.includes(RoleTypes.RoleDEVHR) ||
      teamLeader
    );
  }, [roles, teamLeader]);

  useEffect(() => {
    if (isAuthenticated && roles.length) {
      if (
        hideTabsForPrivilegedUsers &&
        ['/timesheets', '/reports', '/adminpanel'].includes(location.pathname)
      ) {
        history.push('/worktime');
      }
    }
  }, [
    hideTabsForPrivilegedUsers,
    history,
    isAuthenticated,
    roles,
    location.pathname,
  ]);

  if (!isAuthenticated || loading) {
    return <LinearProgress />;
  }

  return (
    <IntlProvider
      messages={locales[currentLocale].messages}
      locale={locales[currentLocale].locale}
      defaultLocale={'en-gb'}
    >
      <Header
        onLogOut={logOut}
        authenticated={isAuthenticated}
        setOpenMenu={setOpenMenu}
        data={getDrawerData()}
      />
      <MuiPickersUtilsProvider
        utils={MondayMomentUtils}
        libInstance={moment}
        locale={'en-gb'}
      >
        <ModalProvider>
          <BodyWrapper>
            <Modal />
            <Box
              minHeight="calc(100vh - 100px)"
              justifyContent="top"
              flexDirection="column"
              position="relative"
              display="flex"
            >
              {roles.length > 0 &&
                isAuthenticated &&
                isWorkOrdersPath(location.pathname) && (
                  <WorkOrderTabs access={workOrderAccess} />
                )}
              {roles.length && isAuthenticated && (
                <Switch>
                  {renderLegacyRedirects()}
                  {routes
                    .filter((route) => {
                      if (route.path === '/timesheets') {
                        return !hideTabsForPrivilegedUsers;
                      } else if (route.path === '/reports') {
                        return !hideTabsForPrivilegedUsers;
                      } else if (route.path === '/adminpanel') {
                        return !hideTabsForPrivilegedUsers;
                      } else {
                        return true;
                      }
                    })
                    .map((route, index) => (
                      <Route
                        key={`${index} - ${route.path}`}
                        component={route.component}
                        exact
                        path={route.path}
                      />
                    ))}
                </Switch>
              )}
            </Box>
          </BodyWrapper>
        </ModalProvider>
        <ToastProvider />
        {isAuthenticated && (
          <Drawer
            variant="temporary"
            open={openMenu}
            onClose={toggleDrawer}
            anchor="right"
            onClick={toggleDrawer}
            data={getDrawerData()}
          />
        )}
      </MuiPickersUtilsProvider>
    </IntlProvider>
  );
}

export default App;
